import React, { ChangeEvent, Fragment, useEffect, useMemo, useState } from "react";
import moment from 'moment';

import { Building2, Layers, Search, Ticket, Users } from "lucide-react";

import { useGetSettingsOverview } from "../../hooks/settings-cax";

import { TextInput } from "../shared/Inputs";

import { EProduct } from "../../types/misc.enum";
import { IEntitlementsData } from "../../types/misc.interface";
import { ISettingsClientAdminListItem } from "../../types/services.interfaces/settings-cax";

const formatEnumLabel = (value: string) => {
    return value
        .split(/[-_]/)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};

export const Settings = () => {
    const { data: overview, isLoading } = useGetSettingsOverview();

    return (
        <div className='settings__page'>
            <p className='text-4xl text-semibold'>Settings</p>
            <p className='text-sm color--txt-muted settings__page__subtitle'>
                Organisation details, entitlements, and client admin access.
            </p>

            {isLoading && (
                <div className="settings__loading">
                    <div className='list-loader' />
                    <p className="text-medium">Loading Settings...</p>
                </div>
            )}

            {!isLoading && !overview && (
                <div className="settings__empty">
                    <p className="text-medium">Unable to load settings.</p>
                </div>
            )}

            {!isLoading && overview && (
                <Fragment>
                    <section className="settings__section">
                        <p className="text-xl text-semibold">Client details</p>
                        <div className="card card--md card--radius-sm settings__details">
                            <div className="settings__details__item">
                                <p className="text-xs text-medium color--txt-disabled">Company name</p>
                                <p className="text-sm text-medium">{overview.client.company_name}</p>
                            </div>
                            <div className="settings__details__item">
                                <p className="text-xs text-medium color--txt-disabled">Domain</p>
                                <p className="text-sm text-medium">{overview.client.domain}</p>
                            </div>
                            <div className="settings__details__item">
                                <p className="text-xs text-medium color--txt-disabled">Name</p>
                                <p className="text-sm text-medium">{overview.current_user.first_name} {overview.current_user.last_name}</p>
                            </div>
                            <div className="settings__details__item">
                                <p className="text-xs text-medium color--txt-disabled">Email</p>
                                <p className="text-sm text-medium">{overview.current_user.email}</p>
                            </div>
                            <div className="settings__details__item">
                                <p className="text-xs text-medium color--txt-disabled">Cloud management</p>
                                <p className="text-sm text-medium">{formatEnumLabel(overview.client.cloud_management)}</p>
                            </div>
                            <div className="settings__details__item">
                                <p className="text-xs text-medium color--txt-disabled">Deployed</p>
                                <p className="text-sm text-medium">{overview.client.deployed ? 'Yes' : 'No'}</p>
                            </div>
                        </div>
                    </section>

                    <section className="settings__section">
                        <p className="text-xl text-semibold">Organisation</p>
                        <div className="settings__stats">
                            <StatCard icon={<Building2 />} label="Sub clients" value={overview.sub_client_count} />
                            <StatCard icon={<Layers />} label="Cohorts" value={overview.cohort_count} />
                            <StatCard icon={<Users />} label="Participants" value={overview.participant_count} />
                            <GuestSessionsCard
                                created={overview.guest_sessions_created}
                                allowed={overview.guest_sessions_allowed}
                            />
                        </div>
                    </section>

                    <GrantedEntitlements entitlements={overview.entitlements} />

                    <section className="settings__section">
                        <p className="text-xl text-semibold">Client admins</p>
                        <ClientAdminsTable clientAdmins={overview.client_admins} />
                    </section>
                </Fragment>
            )}
        </div>
    );
};

const StatCard = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) => {
    return (
        <div className="card card--md card--radius-sm settings__stat_card">
            <div className="settings__stat_card__icon">{icon}</div>
            <p className="text-3xl text-semibold">{value}</p>
            <p className="text-sm text-medium color--txt-muted">{label}</p>
        </div>
    );
};

const GuestSessionsCard = ({ created, allowed }: { created: number; allowed: number | null }) => {
    const allowedLabel = allowed === null ? 'Unlimited' : allowed.toString();
    const usagePct = allowed && allowed > 0 ? Math.min(100, Math.round((created / allowed) * 100)) : null;

    return (
        <div className="card card--md card--radius-sm settings__stat_card">
            <div className="settings__stat_card__icon"><Ticket /></div>
            <p className="text-3xl text-semibold">
                {created}
                <span className="text-lg text-medium color--txt-muted"> / {allowedLabel}</span>
            </p>
            <p className="text-sm text-medium color--txt-muted">Guest sessions</p>
            {usagePct !== null && (
                <div className="settings__stat_card__meter" aria-label={`${usagePct}% of guest sessions used`}>
                    <span style={{ width: `${usagePct}%` }} />
                </div>
            )}
        </div>
    );
};

const GrantedEntitlements = ({ entitlements }: { entitlements: IEntitlementsData }) => {
    const products = useMemo(() => entitlements.products ?? [], [entitlements.products]);
    const [activeProductKey, setActiveProductKey] = useState<EProduct | null>(products[0]?.key ?? null);

    useEffect(() => {
        if (!products.length) {
            setActiveProductKey(null);
            return;
        };

        if (!activeProductKey || !products.some(product => product.key === activeProductKey)) {
            setActiveProductKey(products[0].key);
        };
    }, [products, activeProductKey]);

    const activeProduct = products.find(product => product.key === activeProductKey) ?? products[0];
    const grantedModules = activeProduct?.key === EProduct.MBTS
        ? entitlements.mbts_modules
        : activeProduct?.key === EProduct.MCSA
            ? entitlements.mcsa_modules
            : [];
    const grantedAssessments = activeProduct?.key === EProduct.MBTS
        ? entitlements.mbts_assessments
        : activeProduct?.key === EProduct.MCSA
            ? entitlements.mcsa_assessments
            : 0;

    return (
        <section className="settings__section settings__entitlements">
            <p className="text-xl text-semibold">Entitlements</p>
            <p className="text-sm color--txt-muted">Granted to this organisation by MindShield admin.</p>
            <div className="settings__entitlements__card">
                {!products.length && (
                    <p className="text-sm color--txt-muted settings__entitlements__empty">No products granted to this organisation.</p>
                )}
                {!!products.length && activeProduct && (
                    <Fragment>
                        <div className="settings__entitlements__tabs">
                            {products.map(({ key, title }) => (
                                <button
                                    key={key}
                                    type="button"
                                    className={`settings__entitlements__tabs__btn ${activeProduct.key === key ? 'active' : ''}`}
                                    onClick={() => setActiveProductKey(key)}
                                >
                                    {title}
                                </button>
                            ))}
                        </div>
                        <div className="settings__entitlements__body">
                            <div className="settings__entitlements__assessments">
                                <p className="text-xs text-medium color--txt-disabled">Assessments</p>
                                <p className="text-3xl text-semibold">{grantedAssessments ?? 0}</p>
                            </div>
                            <div className="settings__entitlements__modules">
                                <p className="text-xs text-medium color--txt-disabled">Modules</p>
                                {!grantedModules.length && (
                                    <p className="text-sm color--txt-muted">No modules granted.</p>
                                )}
                                {!!grantedModules.length && (
                                    <div className="settings__entitlements__chips">
                                        {grantedModules.map(module => (
                                            <span key={module.id} className="settings__entitlements__chip">{module.title}</span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </Fragment>
                )}
            </div>
        </section>
    );
};

const ClientAdminsTable = ({ clientAdmins }: { clientAdmins: ISettingsClientAdminListItem[] }) => {
    const [filterInputValue, setFilterInputValue] = useState('');
    const [filteredAdmins, setFilteredAdmins] = useState<ISettingsClientAdminListItem[]>(clientAdmins);

    useEffect(() => {
        setFilteredAdmins(clientAdmins);
        setFilterInputValue('');
    }, [clientAdmins]);

    const handleFilter = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFilterInputValue(value);
        const query = value.toLowerCase();
        setFilteredAdmins(clientAdmins.filter(admin => {
            const name = `${admin.first_name} ${admin.last_name}`.toLowerCase();
            return name.includes(query) || admin.email.toLowerCase().includes(query) || admin.role.toLowerCase().includes(query);
        }));
    };

    const hasAdmins = clientAdmins.length > 0;

    return (
        <Fragment>
            <div className="settings__admins__filters">
                <TextInput
                    icon={{ icon: <Search />, position: 'prefix' }}
                    placeholder="Search"
                    value={filterInputValue}
                    onChange={handleFilter}
                    disabled={!hasAdmins}
                />
            </div>
            {!hasAdmins && (
                <div className="settings__empty">
                    <p className="text-medium">No client admins yet!</p>
                </div>
            )}
            {hasAdmins && !filteredAdmins.length && (
                <div className="settings__empty">
                    <p className="text-medium">No client admins found.</p>
                </div>
            )}
            {!!filteredAdmins.length && (
                <div className="settings__admins__table_wrapper">
                    <table>
                        <thead>
                            <tr>
                                <th><p className="text-xs text-medium color--txt-disabled text-left">Name</p></th>
                                <th><p className="text-xs text-medium color--txt-disabled text-left">Email</p></th>
                                <th><p className="text-xs text-medium color--txt-disabled text-center">Role</p></th>
                                <th><p className="text-xs text-medium color--txt-disabled text-center">Sub client</p></th>
                                <th><p className="text-xs text-medium color--txt-disabled text-center">Status</p></th>
                                <th><p className="text-xs text-medium color--txt-disabled text-center">Last login</p></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredAdmins.map(admin => (
                                <tr key={admin.id} className={admin.is_current_user ? 'settings__admins__row--current' : ''}>
                                    <td>
                                        <div className="settings__admins__name">
                                            <p className="text-sm text-medium">{admin.first_name} {admin.last_name}</p>
                                            {admin.is_current_user && <span className="settings__admins__you">You</span>}
                                        </div>
                                    </td>
                                    <td><p className="text-sm color--txt-muted">{admin.email}</p></td>
                                    <td><p className="text-sm color--txt-muted text-center">{formatEnumLabel(admin.role)}</p></td>
                                    <td><p className="text-sm color--txt-muted text-center">{admin.sub_client_name ?? '-'}</p></td>
                                    <td>
                                        <p className={`text-sm text-center ${admin.logged_in ? 'color--success-strong' : 'color--txt-muted'}`}>
                                            {admin.logged_in ? 'Online' : 'Offline'}
                                        </p>
                                    </td>
                                    <td><p className="text-sm color--txt-muted text-center">{admin.last_login_at ? moment(admin.last_login_at).format('MMM DD, YYYY, h:mm A') : 'Never'}</p></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Fragment>
    );
};
